import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import {
  Table,
  Input,
  Button,
  Space,
  Row,
  Col,
  Typography,
  Modal,
  Form,
  message,
  Card,
  Avatar,
  Tooltip,
  Upload,
} from "antd";
import type { InputRef } from "antd";
import moment from "moment";
import {
  SearchOutlined,
  UserAddOutlined,
  TeamOutlined,
  PhoneOutlined,
  CalendarOutlined,
  DeleteOutlined,
  EditOutlined,
  UserOutlined,
  UsergroupDeleteOutlined,
  ShopOutlined,
  SafetyCertificateOutlined,
  DownloadOutlined,
  UploadOutlined,
  InboxOutlined,
} from "@ant-design/icons";
import Highlighter from "react-highlight-words";
import debounce from "lodash.debounce";
import {
  FETCH_ALL_WAREHOUSE_MEMBERS,
  CREATE_WAREHOUSE_MEMBER,
  UPDATE_WAREHOUSE_MEMBER,
  DELETE_WAREHOUSE_MEMBER,
} from "../../services/apiEndPoints";
import { IWarehouseMember } from "../types/warehouseMember.types";
import axios from "../../services/axiosInstance";

const { Title, Text } = Typography;

const STAT_CARDS = [
  {
    key: "total",
    label: "Total Members",
    icon: <TeamOutlined />,
    color: "#1677ff",
    bg: "linear-gradient(135deg, #eff6ff, #dbeafe)",
    border: "#93c5fd",
  },
  {
    key: "withPhone",
    label: "With Phone",
    icon: <PhoneOutlined />,
    color: "#10b981",
    bg: "linear-gradient(135deg, #f0fdf4, #dcfce7)",
    border: "#6ee7b7",
  },
  {
    key: "recentlyAdded",
    label: "Last 30 Days",
    icon: <CalendarOutlined />,
    color: "#f59e0b",
    bg: "linear-gradient(135deg, #fffbeb, #fef3c7)",
    border: "#fcd34d",
  },
  {
    key: "addedToday",
    label: "Added Today",
    icon: <UserAddOutlined />,
    color: "#8b5cf6",
    bg: "linear-gradient(135deg, #f5f3ff, #ede9fe)",
    border: "#c4b5fd",
  },
];

const WAREHOUSE_MEMBER_CSV_FIELDS = ["name", "phoneNumber"] as const;

const WarehouseMembersTableComponent = () => {
  const [searchText, setSearchText] = useState("");
  const [searchedColumn, setSearchedColumn] = useState("");
  const [globalSearch, setGlobalSearch] = useState("");
  const [data, setData] = useState<IWarehouseMember[]>([]);
  const searchInput = useRef<InputRef>(null);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingMember, setEditingMember] =
    useState<IWarehouseMember | null>(null);
  const [addForm] = Form.useForm();
  const [editForm] = Form.useForm();
  const [addLoading, setAddLoading] = useState(false);
  const [editLoading, setEditLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [isBulkModalOpen, setIsBulkModalOpen] = useState(false);
  const [bulkRows, setBulkRows] = useState<Partial<IWarehouseMember>[]>([]);
  const [bulkFileName, setBulkFileName] = useState("");
  const [bulkUploading, setBulkUploading] = useState(false);

  const fetchMembers = async () => {
    try {
      setLoading(true);
      const res = await axios.get(FETCH_ALL_WAREHOUSE_MEMBERS);
      const body = res?.data;
      setData(Array.isArray(body) ? body : body?.results ?? []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMembers();
  }, []);

  const handleSearch = useMemo(
    () =>
      debounce((selectedKeys, confirm, dataIndex) => {
        confirm();
        setSearchText(selectedKeys[0]);
        setSearchedColumn(dataIndex);
      }, 300),
    []
  );

  const handleAddMember = useCallback(async () => {
    try {
      const values = await addForm.validateFields();
      setAddLoading(true);
      await axios.post(CREATE_WAREHOUSE_MEMBER, values);
      message.success("Warehouse member added successfully");
      addForm.resetFields();
      setIsAddModalOpen(false);
      await fetchMembers();
    } catch (error: any) {
      const msg =
        error?.response?.data?.error || error?.response?.data?.message;
      if (msg) message.error(msg);
    } finally {
      setAddLoading(false);
    }
  }, [addForm]);

  const handleEditMember = useCallback(async () => {
    if (!editingMember) return;
    try {
      const values = await editForm.validateFields();
      setEditLoading(true);
      await axios.put(
        UPDATE_WAREHOUSE_MEMBER(editingMember.warehouseMemberId),
        values
      );
      message.success("Warehouse member updated successfully");
      editForm.resetFields();
      setIsEditModalOpen(false);
      setEditingMember(null);
      await fetchMembers();
    } catch (error: any) {
      const msg =
        error?.response?.data?.error || error?.response?.data?.message;
      if (msg) message.error(msg);
    } finally {
      setEditLoading(false);
    }
  }, [editForm, editingMember]);

  const downloadCSV = useCallback(() => {
    if (!data.length) {
      message.warning("No data to export");
      return;
    }
    const headers = [...WAREHOUSE_MEMBER_CSV_FIELDS, "createdAt"];
    const rows = data.map((m) =>
      headers.map((h) => String((m as any)[h] ?? ""))
    );
    const csv = [
      headers.join(","),
      ...rows.map((r) =>
        r.map((c) => `"${c.replace(/"/g, '""')}"`).join(",")
      ),
    ].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `warehouse_members_${moment().format(
      "YYYY_MM_DD_HH_mm"
    )}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    message.success("CSV downloaded");
  }, [data]);

  const downloadTemplate = useCallback(() => {
    const headers = WAREHOUSE_MEMBER_CSV_FIELDS.join(",");
    const sample = ["Ravi Kumar", "9876543210"]
      .map((c) => `"${c}"`)
      .join(",");
    const csv = `${headers}\n${sample}\n`;
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "warehouse_member_template.csv";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }, []);

  const parseCSV = (text: string): Record<string, string>[] => {
    const lines = text
      .replace(/\r/g, "")
      .split("\n")
      .filter((l) => l.trim());
    if (!lines.length) return [];
    const splitLine = (line: string): string[] => {
      const out: string[] = [];
      let cur = "";
      let inQuotes = false;
      for (let i = 0; i < line.length; i++) {
        const ch = line[i];
        if (inQuotes) {
          if (ch === '"' && line[i + 1] === '"') {
            cur += '"';
            i++;
          } else if (ch === '"') {
            inQuotes = false;
          } else {
            cur += ch;
          }
        } else {
          if (ch === '"') inQuotes = true;
          else if (ch === ",") {
            out.push(cur);
            cur = "";
          } else cur += ch;
        }
      }
      out.push(cur);
      return out.map((c) => c.trim());
    };
    const headers = splitLine(lines[0]);
    return lines.slice(1).map((line) => {
      const cells = splitLine(line);
      const row: Record<string, string> = {};
      headers.forEach((h, i) => {
        row[h] = cells[i] ?? "";
      });
      return row;
    });
  };

  const handleCSVFile = (file: File) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const text = String(e.target?.result || "");
        const parsed = parseCSV(text);
        const valid = parsed.filter((r) => r.name && r.phoneNumber);
        if (!valid.length) {
          message.error(
            "No valid rows found. Each row needs name and phoneNumber."
          );
          return;
        }
        setBulkRows(valid);
        setBulkFileName(file.name);
        if (valid.length < parsed.length) {
          message.warning(
            `${parsed.length - valid.length} row(s) skipped due to missing name/phoneNumber`
          );
        }
      } catch {
        message.error("Failed to parse CSV");
      }
    };
    reader.readAsText(file);
    return false;
  };

  const handleBulkUpload = useCallback(async () => {
    if (!bulkRows.length) {
      message.warning("No rows to upload");
      return;
    }
    setBulkUploading(true);
    const results = await Promise.allSettled(
      bulkRows.map((row) => axios.post(CREATE_WAREHOUSE_MEMBER, row))
    );
    const ok = results.filter((r) => r.status === "fulfilled").length;
    const failed = results.length - ok;
    if (ok) message.success(`${ok} member(s) uploaded`);
    if (failed) message.error(`${failed} row(s) failed`);
    setBulkUploading(false);
    setBulkRows([]);
    setBulkFileName("");
    setIsBulkModalOpen(false);
    await fetchMembers();
  }, [bulkRows]);

  const handleDelete = useCallback(async (warehouseMemberId: string) => {
    try {
      await axios.delete(DELETE_WAREHOUSE_MEMBER(warehouseMemberId));
      message.success("Warehouse member deleted successfully");
      await fetchMembers();
    } catch {
      message.error("Failed to delete warehouse member");
    }
  }, []);

  const openEditModal = useCallback(
    (member: IWarehouseMember) => {
      setEditingMember(member);
      editForm.setFieldsValue({
        name: member.name,
        phoneNumber: member.phoneNumber,
      });
      setIsEditModalOpen(true);
    },
    [editForm]
  );

  const getColumnSearchProps = useCallback(
    (dataIndex: string) => ({
      filterDropdown: ({
        setSelectedKeys,
        selectedKeys,
        confirm,
        clearFilters = () => {},
      }: any) => (
        <div style={{ padding: 8, width: 240 }}>
          <Input
            ref={searchInput}
            placeholder={`Search ${dataIndex}`}
            value={selectedKeys[0]}
            onChange={(e) =>
              setSelectedKeys(e.target.value ? [e.target.value] : [])
            }
            onPressEnter={() => handleSearch(selectedKeys, confirm, dataIndex)}
            style={{ marginBottom: 8 }}
          />
          <Space>
            <Button
              type="primary"
              size="small"
              icon={<SearchOutlined />}
              onClick={() => handleSearch(selectedKeys, confirm, dataIndex)}
            >
              Search
            </Button>
            <Button
              size="small"
              onClick={() => {
                clearFilters();
                setSearchText("");
              }}
            >
              Reset
            </Button>
          </Space>
        </div>
      ),
      filterIcon: (filtered: any) => (
        <SearchOutlined style={{ color: filtered ? "#1677ff" : undefined }} />
      ),
      onFilter: (value: any, record: IWarehouseMember) =>
        record[dataIndex as keyof IWarehouseMember]
          ?.toString()
          .toLowerCase()
          .includes(String(value).toLowerCase()),
      render: (text: any) =>
        searchedColumn === dataIndex ? (
          <Highlighter
            highlightStyle={{ backgroundColor: "#ffc069", padding: 0 }}
            searchWords={[searchText]}
            autoEscape
            textToHighlight={text?.toString() || ""}
          />
        ) : (
          <span style={{ whiteSpace: "nowrap" }}>{text}</span>
        ),
    }),
    [searchText, searchedColumn, handleSearch]
  );

  const filteredData = useMemo(() => {
    if (!globalSearch) return data;
    return data.filter((item) =>
      Object.values(item)
        .join(" ")
        .toLowerCase()
        .includes(globalSearch.toLowerCase())
    );
  }, [data, globalSearch]);

  const stats = useMemo(
    () => ({
      total: data.length,
      withPhone: data.filter((d) => d.phoneNumber).length,
      recentlyAdded: data.filter(
        (d) => moment().diff(moment(d.createdAt), "days") <= 30
      ).length,
      addedToday: data.filter((d) =>
        moment(d.createdAt).isSame(moment(), "day")
      ).length,
    }),
    [data]
  );

  const columns = useMemo(
    () => [
      {
        title: (
          <>
            <UserOutlined style={{ fontSize: 12 }} /> Name
          </>
        ),
        dataIndex: "name",
        key: "name",
        ...getColumnSearchProps("name"),
        render: (name: string, record: IWarehouseMember) => (
          <Space>
            <Avatar
              size={32}
              style={{
                background: "linear-gradient(135deg, #1677ff, #4096ff)",
                fontSize: 13,
                fontWeight: 600,
              }}
            >
              {name
                ?.split(" ")
                .map((n) => n[0])
                .join("")
                .toUpperCase()
                .slice(0, 2)}
            </Avatar>
            <div>
              <Text strong style={{ display: "block", lineHeight: 1.3 }}>
                {name}
              </Text>
              <Text
                type="secondary"
                style={{ fontSize: 12, display: "block", lineHeight: 1.3 }}
              >
                {record.phoneNumber}
              </Text>
            </div>
          </Space>
        ),
      },
      {
        title: (
          <>
            <PhoneOutlined style={{ fontSize: 12 }} /> Phone
          </>
        ),
        dataIndex: "phoneNumber",
        key: "phoneNumber",
        width: 180,
        ...getColumnSearchProps("phoneNumber"),
        render: (phone: string) =>
          phone ? (
            <Text style={{ fontSize: 13 }}>{phone}</Text>
          ) : (
            <Text type="secondary" style={{ fontSize: 12 }}>
              --
            </Text>
          ),
      },
      {
        title: (
          <>
            <CalendarOutlined style={{ fontSize: 12 }} /> Added
          </>
        ),
        dataIndex: "createdAt",
        key: "createdAt",
        width: 160,
        sorter: (a: IWarehouseMember, b: IWarehouseMember) =>
          moment(a.createdAt).unix() - moment(b.createdAt).unix(),
        render: (createdAt: string) => (
          <div>
            <Text style={{ whiteSpace: "nowrap", fontSize: 13 }}>
              {moment(createdAt).format("MMM D, YYYY")}
            </Text>
            <br />
            <Text type="secondary" style={{ fontSize: 11 }}>
              {moment(createdAt).fromNow()}
            </Text>
          </div>
        ),
      },
      {
        title: (
          <>
            <CalendarOutlined style={{ fontSize: 12 }} /> Updated
          </>
        ),
        dataIndex: "updatedAt",
        key: "updatedAt",
        width: 160,
        render: (updatedAt: string) => (
          <Text type="secondary" style={{ fontSize: 12 }}>
            {moment(updatedAt).fromNow()}
          </Text>
        ),
      },
      {
        title: (
          <>
            <SafetyCertificateOutlined style={{ fontSize: 12 }} /> Actions
          </>
        ),
        key: "actions",
        width: 100,
        align: "center" as const,
        render: (_: any, record: IWarehouseMember) => (
          <Space size={4}>
            <Tooltip title="Edit member">
              <Button
                type="text"
                size="small"
                icon={<EditOutlined />}
                onClick={() => openEditModal(record)}
              />
            </Tooltip>
            <Tooltip title="Delete member">
              <Button
                type="text"
                size="small"
                danger
                icon={<DeleteOutlined />}
                onClick={() =>
                  Modal.confirm({
                    title: "Delete Warehouse Member",
                    content: `Are you sure you want to delete ${record.name}? This will deactivate the record.`,
                    okText: "Delete",
                    okType: "danger",
                    onOk: () => handleDelete(record.warehouseMemberId),
                  })
                }
              />
            </Tooltip>
          </Space>
        ),
      },
    ],
    [getColumnSearchProps, handleDelete, openEditModal]
  );

  const memberFormFields = (
    <>
      <Form.Item
        label="Name"
        name="name"
        rules={[{ required: true, message: "Please enter member name" }]}
      >
        <Input
          prefix={<UserOutlined style={{ color: "#94a3b8" }} />}
          placeholder="Full name"
        />
      </Form.Item>
      <Form.Item
        label="Phone Number"
        name="phoneNumber"
        rules={[
          { required: true, message: "Please enter phone number" },
          {
            validator: (_, value) => {
              if (!value) return Promise.resolve();
              if (/^\d{10}$/.test(value.trim())) return Promise.resolve();
              return Promise.reject(
                new Error("Phone number must be exactly 10 digits")
              );
            },
          },
        ]}
      >
        <Input
          prefix={<PhoneOutlined style={{ color: "#94a3b8" }} />}
          placeholder="9876543210"
          maxLength={10}
        />
      </Form.Item>
    </>
  );

  return (
    <div>
      {/* Page header */}
      <Row justify="space-between" align="middle" className="page-header-row">
        <Col>
          <Title level={4} className="page-title">
            <ShopOutlined style={{ marginRight: 8, color: "#1677ff" }} />
            Warehouse Members
          </Title>
          <Text className="page-subtitle">
            Manage warehouse member profiles and contact information
          </Text>
        </Col>
        <Col>
          <Space wrap>
            <Button
              icon={<DownloadOutlined />}
              onClick={downloadCSV}
              disabled={!data.length}
            >
              Export CSV
            </Button>
            <Button
              icon={<UploadOutlined />}
              onClick={() => setIsBulkModalOpen(true)}
            >
              Bulk Upload
            </Button>
            <Button
              type="primary"
              icon={<UserAddOutlined />}
              onClick={() => setIsAddModalOpen(true)}
            >
              Add Member
            </Button>
          </Space>
        </Col>
      </Row>

      {/* Stat cards */}
      <Row gutter={[12, 12]} style={{ marginBottom: 20 }}>
        {STAT_CARDS.map((card) => (
          <Col key={card.key} xs={12} sm={12} md={6}>
            <Card
              className="stat-card"
              style={{
                background: card.bg,
                border: `1px solid ${card.border}`,
              }}
              styles={{ body: { padding: "14px 16px" } }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <div
                  style={{
                    width: 36,
                    height: 36,
                    borderRadius: 10,
                    background: `${card.color}18`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: 18,
                    color: card.color,
                    flexShrink: 0,
                  }}
                >
                  {card.icon}
                </div>
                <div>
                  <div
                    style={{
                      fontSize: 22,
                      fontWeight: 700,
                      color: card.color,
                      lineHeight: 1.1,
                    }}
                  >
                    {stats[card.key as keyof typeof stats]}
                  </div>
                  <div
                    style={{
                      fontSize: 11,
                      color: "#64748b",
                      fontWeight: 500,
                      lineHeight: 1.3,
                    }}
                  >
                    {card.label}
                  </div>
                </div>
              </div>
            </Card>
          </Col>
        ))}
      </Row>

      {/* Toolbar + Table */}
      <div>
        <div className="table-toolbar">
          <Space wrap size={8}>
            <Input
              allowClear
              prefix={<SearchOutlined style={{ color: "#94a3b8" }} />}
              placeholder="Search warehouse members..."
              style={{ width: 260 }}
              value={globalSearch}
              onChange={(e) => setGlobalSearch(e.target.value)}
            />
          </Space>
        </div>

        <Table<IWarehouseMember>
          rowKey="warehouseMemberId"
          size="middle"
          columns={columns as any}
          dataSource={filteredData}
          loading={{
            spinning: loading,
            tip: "Loading warehouse members...",
          }}
          locale={{
            emptyText: (
              <div style={{ padding: "40px 0" }}>
                <UsergroupDeleteOutlined
                  style={{ fontSize: 42, color: "#cbd5e1", marginBottom: 12 }}
                />
                <div
                  style={{ color: "#64748b", fontSize: 14, fontWeight: 500 }}
                >
                  No warehouse members found
                </div>
                <div style={{ color: "#94a3b8", fontSize: 13, marginTop: 4 }}>
                  {globalSearch
                    ? "Try adjusting your search"
                    : "Add a member to get started"}
                </div>
              </div>
            ),
          }}
          pagination={{
            defaultPageSize: 10,
            showSizeChanger: true,
            pageSizeOptions: ["10", "20", "50"],
            showTotal: (total, range) => (
              <Text style={{ color: "#64748b", fontSize: 13 }}>
                Showing <Text strong>{range[0]}-{range[1]}</Text> of{" "}
                <Text strong>{total}</Text> member{total !== 1 ? "s" : ""}
              </Text>
            ),
          }}
          scroll={{ x: "max-content" }}
        />
      </div>

      {/* Add Member Modal */}
      <Modal
        title={
          <>
            <UserAddOutlined style={{ marginRight: 8, color: "#1677ff" }} />
            Add Warehouse Member
          </>
        }
        open={isAddModalOpen}
        onCancel={() => {
          setIsAddModalOpen(false);
          addForm.resetFields();
        }}
        onOk={handleAddMember}
        okText="Add Member"
        confirmLoading={addLoading}
        width={480}
      >
        <Form form={addForm} layout="vertical" requiredMark={false}>
          {memberFormFields}
        </Form>
      </Modal>

      {/* Edit Member Modal */}
      <Modal
        title={
          <>
            <EditOutlined style={{ marginRight: 8, color: "#1677ff" }} />
            Edit Warehouse Member
          </>
        }
        open={isEditModalOpen}
        onCancel={() => {
          setIsEditModalOpen(false);
          setEditingMember(null);
          editForm.resetFields();
        }}
        onOk={handleEditMember}
        okText="Save Changes"
        confirmLoading={editLoading}
        width={480}
      >
        <Text type="secondary" style={{ display: "block", marginBottom: 12 }}>
          {editingMember?.name} — {editingMember?.phoneNumber}
        </Text>
        <Form form={editForm} layout="vertical" requiredMark={false}>
          {memberFormFields}
        </Form>
      </Modal>

      {/* Bulk Upload Modal */}
      <Modal
        title={
          <>
            <UploadOutlined style={{ marginRight: 8, color: "#1677ff" }} />
            Bulk Upload Warehouse Members
          </>
        }
        open={isBulkModalOpen}
        onCancel={() => {
          if (bulkUploading) return;
          setIsBulkModalOpen(false);
          setBulkRows([]);
          setBulkFileName("");
        }}
        onOk={handleBulkUpload}
        okText={`Upload ${bulkRows.length || ""} row${
          bulkRows.length === 1 ? "" : "s"
        }`}
        okButtonProps={{ disabled: !bulkRows.length }}
        confirmLoading={bulkUploading}
        width={520}
      >
        <Space direction="vertical" size={12} style={{ width: "100%" }}>
          <Text type="secondary" style={{ fontSize: 13 }}>
            Upload a CSV with columns:{" "}
            {WAREHOUSE_MEMBER_CSV_FIELDS.join(", ")}. Both fields are required.
          </Text>
          <Button
            size="small"
            icon={<DownloadOutlined />}
            onClick={downloadTemplate}
          >
            Download Template
          </Button>
          <Upload.Dragger
            accept=".csv,text/csv"
            maxCount={1}
            showUploadList={false}
            beforeUpload={handleCSVFile}
          >
            <p className="ant-upload-drag-icon">
              <InboxOutlined />
            </p>
            <p className="ant-upload-text">
              Click or drag CSV file to this area to upload
            </p>
            <p className="ant-upload-hint" style={{ fontSize: 12 }}>
              Only .csv files are supported
            </p>
          </Upload.Dragger>
          {bulkFileName && (
            <Text style={{ fontSize: 12, color: "#1677ff" }}>
              {bulkFileName} — {bulkRows.length} valid row
              {bulkRows.length === 1 ? "" : "s"} ready
            </Text>
          )}
        </Space>
      </Modal>
    </div>
  );
};

export default WarehouseMembersTableComponent;
